import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { resolveWhatsAppNumber } from '../EnquiryList/enquiryUtils';
import { swal } from '../../../../helper/swal';

export const MeetingWhatsAppModal = ({ row, children }) => {
    const [show, setShow] = useState(false);
    const enq = row?.enquiry || {};

    const defaultNumber = useMemo(
        () => resolveWhatsAppNumber(enq.whatsapp, enq.phone) || '',
        [enq.whatsapp, enq.phone]
    );

    const [personName, setPersonName] = useState('');
    const [schoolName, setSchoolName] = useState('');
    const [recipientPhone, setRecipientPhone] = useState('');
    const [meetingLink, setMeetingLink] = useState('');
    const [meetingTime, setMeetingTime] = useState('8:00 AM');
    const [messageText, setMessageText] = useState('');

    // Dynamic message template generator
    const buildMessage = ({ person, school, link, time }) => {
        let greeting = "Dear Sir/Ma'am,";
        const cleanPerson = (person || '').trim();
        if (cleanPerson) {
            if (/(\bsir\b|\bma'?am\b|\bmam\b)/i.test(cleanPerson)) {
                greeting = `Hello ${cleanPerson},`;
            } else {
                greeting = `Hello ${cleanPerson} sir,`;
            }
        }

        const cleanSchool = (school || '').trim() || (enq.name || 'Demo Public School').trim();
        const cleanTime = (time || '').trim() || '8:00 AM';
        const cleanLink = (link || '').trim();

        let codeSection = '';
        if (cleanLink) {
            const match = cleanLink.match(/meet\.google\.com\/([a-zA-Z0-9_-]+)/i);
            if (match && match[1]) {
                codeSection = `\nYou can also open Google Meet and enter the meeting code: ${match[1]}`;
            }
        }

        const linkDisplay = cleanLink || '[Paste Google Meet link here]';

        return `Subject: Google Meet Link for School ERP Demo

${greeting}
As discussed, please find below the Google Meet link for the School ERP demo session for ${cleanSchool}.

Meeting Time: ${cleanTime}

Join Google Meet:
${linkDisplay}${codeSection}

Kindly join the meeting at the scheduled time.

We look forward to connecting with you and demonstrating our School ERP software.

Regards,
Team Unbox Computing Software
Lal Mishra
Shubham Maurya
Jahnavi Mishra`;
    };

    // Initialize state when modal opens
    useEffect(() => {
        if (show) {
            const initialPerson = (enq.father_name || '').trim();
            const initialSchool = (enq.name || '').trim();
            const initialTime = '8:00 AM';

            setPersonName(initialPerson);
            setSchoolName(initialSchool);
            setRecipientPhone(defaultNumber);
            setMeetingLink('');
            setMeetingTime(initialTime);

            setMessageText(
                buildMessage({
                    person: initialPerson,
                    school: initialSchool,
                    link: '',
                    time: initialTime
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // Unified dynamic field update
    const updateField = (field, val) => {
        let p = personName;
        let s = schoolName;
        let l = meetingLink;
        let t = meetingTime;

        if (field === 'person') { p = val; setPersonName(val); }
        if (field === 'school') { s = val; setSchoolName(val); }
        if (field === 'link') { l = val; setMeetingLink(val); }
        if (field === 'time') { t = val; setMeetingTime(val); }

        setMessageText(buildMessage({ person: p, school: s, link: l, time: t }));
    };

    // Open Google Meet in new tab to create instant meeting
    const handleGenerateMeet = () => {
        window.open('https://meet.google.com/new', '_blank', 'noopener,noreferrer');
    };

    // Quick paste link from clipboard
    const handlePasteLink = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text) {
                updateField('link', text.trim());
                swal.success('Meeting link pasted!');
            }
        } catch {
            swal.info('Please press Ctrl+V in the box to paste your link.');
        }
    };

    const extractedCode = useMemo(() => {
        if (!meetingLink) return '';
        const match = meetingLink.match(/meet\.google\.com\/([a-zA-Z0-9_-]+)/i);
        return match && match[1] ? match[1] : '';
    }, [meetingLink]);

    const toggleModal = () => setShow(!show);

    const handleCopy = () => {
        navigator.clipboard.writeText(messageText)
            .then(() => swal.success('Message copied!'))
            .catch(() => swal.error('Failed to copy'));
    };

    const handleSend = (mode = 'web') => {
        const digits = recipientPhone.replace(/\D/g, '');
        if (!digits) return swal.error('Please enter a valid phone number');

        const encoded = encodeURIComponent(messageText);
        const url = mode === 'web'
            ? `https://web.whatsapp.com/send?phone=${digits}&text=${encoded}`
            : `https://api.whatsapp.com/send?phone=${digits}&text=${encoded}`;

        window.open(url, '_blank', 'noopener,noreferrer');
        toggleModal();
    };

    return (
        <>
            {children ? (
                React.isValidElement(children) ? (
                    React.cloneElement(children, {
                        onClick: (e) => {
                            if (children.props.onClick) children.props.onClick(e);
                            toggleModal();
                        }
                    })
                ) : (
                    <span onClick={toggleModal} role="button">{children}</span>
                )
            ) : (
                <button
                    type="button"
                    onClick={toggleModal}
                    className="btn btn-sm btn-soft-success py-1 px-2 border-0 d-inline-flex align-items-center"
                    title="Send Meeting Link on WhatsApp"
                >
                    <i className="ri-whatsapp-line fs-14 text-success" />
                </button>
            )}

            <Modal show={show} onHide={toggleModal} centered size="lg">
                <Modal.Header closeButton className="py-2.5 px-3 bg-light-subtle border-bottom">
                    <div className="d-flex align-items-center gap-2">
                        <div className="avatar-xs rounded-circle bg-soft-success d-flex align-items-center justify-content-center">
                            <i className="ri-whatsapp-fill text-success fs-18" />
                        </div>
                        <div>
                            <h5 className="modal-title fs-14 fw-bold text-dark mb-0">Send Meeting Link via WhatsApp</h5>
                            <span className="text-muted fs-11">{enq.name || 'Candidate / School'}</span>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-3">
                    <div className="row g-2">
                        {/* 1. Contact Person Name */}
                        <div className="col-12 col-md-4">
                            <label className="form-label fs-12 fw-semibold mb-1">
                                <i className="ri-user-star-line text-primary me-1" /> Contact Person / Principal
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm fs-12"
                                placeholder="e.g. Amit (blank for Sir/Ma'am)"
                                value={personName}
                                onChange={(e) => updateField('person', e.target.value)}
                            />
                            <span className="text-muted fs-11 d-block mt-0.5">
                                Greeting: <span className="text-primary fw-medium">
                                    {personName.trim() ? `Hello ${personName.trim()} sir` : "Dear Sir/Ma'am"}
                                </span>
                            </span>
                        </div>

                        {/* 2. School / Client Name */}
                        <div className="col-12 col-md-5">
                            <label className="form-label fs-12 fw-semibold mb-1">
                                <i className="ri-building-line text-primary me-1" /> School / Client Name *
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm fs-12 fw-medium"
                                value={schoolName}
                                onChange={(e) => updateField('school', e.target.value)}
                            />
                        </div>

                        {/* 3. WhatsApp Phone */}
                        <div className="col-12 col-md-3">
                            <label className="form-label fs-12 fw-semibold mb-1">
                                <i className="ri-phone-line text-success me-1" /> WhatsApp Number
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm fs-12"
                                placeholder="e.g. 919876543210"
                                value={recipientPhone}
                                onChange={(e) => setRecipientPhone(e.target.value)}
                            />
                        </div>

                        {/* 4. Meeting Link with New Tab & Paste Buttons */}
                        <div className="col-12 col-md-8">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <label className="form-label fs-12 fw-semibold mb-0">
                                    <i className="ri-link text-primary me-1" /> Meeting Link *
                                </label>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-outline-primary py-0 px-2 fs-11 d-inline-flex align-items-center gap-1"
                                    onClick={handleGenerateMeet}
                                    title="Open Google Meet in new tab to generate meeting"
                                >
                                    <i className="ri-video-chat-fill text-danger fs-12" />
                                    <span>Create Meet (New Tab)</span>
                                    <i className="ri-external-link-line fs-11" />
                                </button>
                            </div>
                            <div className="input-group input-group-sm">
                                <input
                                    type="url"
                                    className="form-control fs-12"
                                    placeholder="https://meet.google.com/xxx-yyyy-zzz"
                                    value={meetingLink}
                                    onChange={(e) => updateField('link', e.target.value)}
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    className="btn btn-light border fs-11 d-inline-flex align-items-center gap-1"
                                    onClick={handlePasteLink}
                                    title="Paste link from clipboard"
                                >
                                    <i className="ri-clipboard-line text-primary" /> Paste Link
                                </button>
                            </div>
                            {extractedCode && (
                                <div className="mt-1">
                                    <span className="badge bg-soft-info text-info fs-10 px-1.5 py-0.5">
                                        Code: <strong>{extractedCode}</strong>
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* 5. Meeting Time */}
                        <div className="col-12 col-md-4">
                            <label className="form-label fs-12 fw-semibold mb-1">
                                <i className="ri-time-line text-warning me-1" /> Meeting Time
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm fs-12"
                                placeholder="8:00 AM"
                                value={meetingTime}
                                onChange={(e) => updateField('time', e.target.value)}
                            />
                        </div>

                        {/* 6. Dynamic Message Textarea (Supports full manual editing) */}
                        <div className="col-12 mt-2">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <label className="form-label fs-12 fw-semibold mb-0">
                                    <i className="ri-message-3-line text-secondary me-1" /> Message Preview (Editable)
                                </label>

                            </div>
                            <textarea
                                className="form-control fs-12 font-monospace"
                                rows={11}
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                style={{ lineHeight: 1.5, backgroundColor: '#fcfcfd' }}
                            />
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className="py-2 px-3 bg-light-subtle d-flex align-items-center justify-content-between">
                    <button type="button" className="btn btn-sm btn-light fs-12" onClick={handleCopy}>
                        <i className="ri-file-copy-line me-1" /> Copy Message
                    </button>
                    <div className="d-flex align-items-center gap-2">
                        <button type="button" className="btn btn-sm btn-secondary fs-12" onClick={toggleModal}>Cancel</button>
                        <button type="button" className="btn btn-sm btn-soft-success fs-12 fw-semibold" onClick={() => handleSend('app')}>
                            <i className="ri-smartphone-line me-1" /> WhatsApp App
                        </button>
                        <button type="button" className="btn btn-sm btn-success fs-12 fw-semibold" onClick={() => handleSend('web')}>
                            <i className="ri-whatsapp-line me-1" /> WhatsApp Web
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MeetingWhatsAppModal;
